import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import t from 'react-translate';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { FormOrgLevelCredlySettings } from 'redux/schemas/models/credly';
import { AccredibleGroupInfo } from 'redux/schemas/models/credly';

export const enableAccredibleSettings = createAsyncThunk(
  'ENABLE_ACCREDIBLE_SETTINGS',
  (payload: { institutionId: number; apiKey: string }) => axios
    .post(
      `institutions/${payload.institutionId}/accredible/configure`,
      {
        apiKey: payload.apiKey,
      },
    )
    .then(response => response.data.result),
);

export const disableAccredibleSettings = createAsyncThunk(
  'DISABLE_ACCREDIBLE_SETTINGS',
  (payload: { institutionId: number; }) => axios
    .put(
      `institutions/${payload.institutionId}/accredible/configure`,
      {
        apiKey: '',
      },
    )
    .then(response => response.data.result),
);

export const saveOrgLevelCredlySettings = createAsyncThunk(
  'SAVE_ORG_LEVEL_CREDLY_SETTINGS',
  (payload: { institutionId: number; form: FormOrgLevelCredlySettings }) => axios
    .post(
      `institutions/${payload.institutionId}/credly/config_create.json`,
      {
        organization_id: payload.form.organizationId,
        organization_token: payload.form.organizationToken,
      },
    )
    .then(response => response.data.result),
);

export const disableOrgLevelCredlySettings = createAsyncThunk(
  'DISABLE_ORG_LEVEL_CREDLY_SETTINGS',
  (payload: { institutionId: number; }) => axios
    .put(
      `institutions/${payload.institutionId}/credly/config_update.json`,
      {
        organization_id: '',
        organization_token: '',
      },
    )
    .then(response => response.data.result),
);

export const validateCredlyBadgeTemplateId = createAsyncThunk(
  'VALIDATE_CREDLY_BADGE_TEMPLATE_ID',
  (payload: { institutionId: number; badgeTemplateId: string; }) => axios
    .get(
      `institutions/${payload.institutionId}/credly/get_badges_template/${payload.badgeTemplateId}`,
    )
    .then(response => response.data),
);

export const enableCredlyBadgeTemplateId = createAsyncThunk(
  'ENABLE_CREDLY_BADGE_TEMPLATE_ID',
  (payload: { institutionId: number; catalogId: string; userEmail: string; badgeTemplateId: string; }) => axios
    .post(
      `institutions/${payload.institutionId}/credly/course/${payload.catalogId}/config_badges_template.json`,
      {
        badge_template_id: payload.badgeTemplateId,
      },
    )
    .then(response => response.data.result),
);

export const removeCredlyBadgeTemplateId = createAsyncThunk(
  'REMOVE_CREDLY_BADGE_TEMPLATE_ID',
  (payload: { institutionId: number; catalogId: string; }) => axios
    .get(
      `institutions/${payload.institutionId}/credly/course/${payload.catalogId}/remove_badges_template.json`,
    )
    .then(response => response.data.result),
);

export const validateAndSaveAccredibleGroupId = createAsyncThunk<
AccredibleGroupInfo,
{ institutionId: number; catalogId: string; groupId: string }
>(
  'VALIDATE_ACCREDIBLE_GROUP_ID',
  async ({ institutionId, catalogId, groupId }, thunkAPI) => {
    try {
      const response = await axios.post(
        `institutions/${institutionId}/accredible/course/${catalogId}/group`,
        { groupId },
      );
      return response.data.result;
    } catch (error) {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const updateAccredibleGroupId = createAsyncThunk<
AccredibleGroupInfo,
{ institutionId: number; catalogId: string; groupId: string }
>(
  'SET_ACCREDIBLE_TO_COURSE',
  async ({ institutionId, catalogId, groupId }, thunkAPI) => {
    try {
      const response = await axios.put(
        `institutions/${institutionId}/accredible/course/${catalogId}/group`,
        { groupId },
      );
      return response.data.result;
    } catch (error) {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const removeAccredibleFromCourse = createAsyncThunk<
void,
{ institutionId: number; catalogId: string }
>(
  'REMOVE_ACCREDIBLE_FROM_COURSE',
  async ({ institutionId, catalogId }, thunkAPI) => {
    try {
      const response = await axios.delete(
        `institutions/${institutionId}/accredible/course/${catalogId}/group`,
      );
      return response.data.result;
    } catch (error) {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const getAccredibleGroup = createAsyncThunk<
AccredibleGroupInfo,
{ institutionId: number; groupId: string }
>(
  'GET_ACCREDIBLE_GROUP',
  async ({ institutionId, groupId }, thunkAPI) => {
    try {
      const response = await axios.get(
        `institutions/${institutionId}/accredible/group/${groupId}`,
      );
      return response.data.result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);


